import _ from 'lodash';

import API from 'lib/RemindAPI';

import Chat from 'entities/Chat';
import ChatMessage from 'entities/ChatMessage';
import ChatMessages from 'collections/ChatMessages';
import Collection from 'lib/Collection';
import { CHATS_PAGE_SIZE } from 'chat/constants/ChatConstants';
import { rootGroup, rootGroupCode } from 'entities/RootGroup';
import { Backbone$Id } from 'types/typescript/Backbone';

class Chats extends Collection<Chat> {
  constructor(models?: Array<Chat>, options: Object = {}) {
    super(models, options);
    this.sort();
  }

  // @ts-ignore - overriding this here so intial state tests can pass.   This should really be `comparator = (a,b) => number`
  comparator(a: Chat, b: Chat): number {
    if (a.isNew()) {
      return -1;
    }
    if (b.isNew()) {
      return 1;
    }
    return b.getSortKey() > a.getSortKey() ? 1 : -1;
  }

  initialize(): void {
    // $FlowIssue
    this.url = '/chats';
    // @ts-ignore - weird modal initlization but keeping it...
    this.model = Chat;
  }

  setPosition(chat: Chat, position: number): void {
    this.remove(chat);
    this.add(chat, { at: position });
  }

  getNewestUpdatedAt(): Date | null {
    const newestChat = _.maxBy(this.models, chat => chat.getUpdatedAt());
    return newestChat ? newestChat.getUpdatedAt() : null;
  }

  getOldestUpdatedAt(): Date | null {
    const oldestChat = _.minBy(this.models, chat => chat.getUpdatedAt());
    return oldestChat ? oldestChat.getUpdatedAt() : null;
  }

  getLastMessage(): ChatMessage | null {
    return this.isEmpty() ? null : this.first().getLastMessage() || null;
  }

  parse(response: any) {
    // HACK to get v2/chats messages in this response
    if (response.messages) {
      const chatsMessages: { [key: string]: ChatMessages } = {};
      for (const chatId in response.messages) {
        // Protect ourselves from a backend bug that includes messages from other threads
        // in the pre-loaded message collection
        // TODO: Remove .filter when https://app.asana.com/0/32507888495125/158289850714897 is fixed
        const messagesData = response.messages[chatId].filter(
          (m: ChatMessage) => m.getChatId() === chatId
        );
        const messagesCollection = ChatMessages.fromJSON(messagesData);
        chatsMessages[chatId] = messagesCollection as ChatMessages;
      }
      // load dynamically to prevent static cyclic dependency in this hack
      const ChatMessageActions = require('chat/actions/ChatMessageActions').default;
      ChatMessageActions.updateChatsMessages(chatsMessages);
    }

    return response.chats;
  }

  selectChatsInGroup(group: { id: string; className?: string }): Chats {
    if (group.className === rootGroupCode || group.id.toString() === rootGroup.id.toString()) {
      return this;
    }
    // new chats aren't associated with any group, they just show up in whatever group
    // you were viewing at time of creation and are deleted if you switch groups
    return new Chats(this.filter(chat => chat.isInGroup(group.id) || chat.isNew()));
  }

  selectGroupChats(): Chats {
    return new Chats(
      this.chain()
        .filter((c: Chat) => c.isGroupType())
        .value()
    );
  }

  static setStateForAllChats(state: string): Promise<void> {
    return API.request('/chats', 'PATCH', {
      chats: { state },
    });
  }

  static enableRepliesForAllChats(): Promise<void> {
    return this.setStateForAllChats('open');
  }

  static disableRepliesForAllChats(): Promise<void> {
    return this.setStateForAllChats('replies_disabled');
  }

  static loadForGroup(groupId: Backbone$Id): Promise<Chats> {
    return Chats.request(null, groupId, false);
  }

  static search(query: string): Promise<Chats> {
    // Include memberships so matching matching against memberships works in chat list
    return Chats.request(query);
  }

  static request(
    query?: string | null,
    groupId: Backbone$Id = rootGroup.id,
    includeMemberships: boolean = true
  ): Promise<Chats> {
    const params = {
      limit: CHATS_PAGE_SIZE,
      // null query sent as empty
      q: query,
      group_id: groupId,
      // We load the root group messages at start so no need to get messages for each group.
      // When we do include messages, only get the unreads, assuming that's all people really care about.
      include_messages: groupId === rootGroup.id ? 'unreads' : false,
      include_memberships: includeMemberships,
    };
    // web's root group means no group to the backend
    if (groupId === rootGroup.id) {
      delete params['group_id'];
    }
    return API.request('/chats', 'GET', params).then(response => Chats.fromJSON(response) as Chats);
  }
}

export default Chats;
